/**
 * Exercise board versions (plan seeds) + runs (attempts).
 * Hot take-phase scalars stay on SessionJson for Clock piggyback; this module owns
 * version/run history and seed apply helpers.
 */
import type {
  BoardVersion,
  BoardVersionSeed,
  ExerciseRun,
  ScenarioSession,
} from './types'
import { buildChatForNewRun } from './runChat'

export type { BoardVersion, BoardVersionSeed, ExerciseRun }

export const MAIN_VERSION_ID = 'version-main'
export const SULLY_PLUS_30_VERSION_ID = 'version-sully-plus-30'

export function newVersionId(prefix = 'version'): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`
}

export function newRunId(): string {
  return `run-${Math.random().toString(36).slice(2, 10)}`
}

export function captureBoardSeed(session: ScenarioSession): BoardVersionSeed {
  const script = (session.comments ?? []).filter((c) => c.source === 'script')
  const priorScript =
    script.length > 0
      ? script
      : findBoardVersion(session.boardVersions, session.selectedVersionId)?.seed.comments?.filter(
          (c) => c.source === 'script'
        ) ?? []
  return {
    objects: structuredClone(session.objects ?? []),
    timeline: structuredClone(session.timeline ?? []),
    camera: { ...session.camera },
    cameraBeats: session.cameraBeats ? structuredClone(session.cameraBeats) : undefined,
    activeMapId: session.activeMapId,
    ...(priorScript.length ? { comments: structuredClone(priorScript) } : {}),
  }
}

export function applyBoardSeed(session: ScenarioSession, seed: BoardVersionSeed): ScenarioSession {
  const script = seed.comments?.filter((c) => c.source === 'script') ?? []
  return {
    ...session,
    objects: structuredClone(seed.objects ?? []),
    timeline: structuredClone(seed.timeline ?? []),
    camera: { ...seed.camera },
    cameraBeats: seed.cameraBeats ? structuredClone(seed.cameraBeats) : undefined,
    ...(seed.activeMapId ? { activeMapId: seed.activeMapId } : {}),
    // Keep session.comments as the authored script bag (not the live run thread).
    comments: structuredClone(script),
  }
}

export function findBoardVersion(
  versions: readonly BoardVersion[] | undefined,
  versionId: string | null | undefined
): BoardVersion | null {
  if (!versionId || !versions?.length) return null
  return versions.find((v) => v.id === versionId) ?? null
}

export function selectedBoardVersion(session: ScenarioSession): BoardVersion | null {
  const versions = session.boardVersions ?? []
  const id = session.selectedVersionId ?? versions[0]?.id
  return findBoardVersion(versions, id)
}

/** Ensure at least Main version exists; wrap current board as seed when missing. */
export function ensureBoardVersions(
  session: ScenarioSession,
  actor?: { userId?: string | null; name?: string | null }
): ScenarioSession {
  if (session.boardVersions?.length) {
    const selected =
      session.selectedVersionId &&
      session.boardVersions.some((v) => v.id === session.selectedVersionId)
        ? session.selectedVersionId
        : session.boardVersions[0]!.id
    // Legacy default label was bare "Main".
    const versions = session.boardVersions.map((v) =>
      v.id === MAIN_VERSION_ID && v.name === 'Main' ? { ...v, name: 'Main version' } : v,
    )
    const renamed = versions.some((v, i) => v !== session.boardVersions![i])
    if (!renamed && selected === session.selectedVersionId) return session
    return {
      ...session,
      boardVersions: renamed ? versions : session.boardVersions,
      selectedVersionId: selected,
    }
  }
  const now = new Date().toISOString()
  const main: BoardVersion = {
    id: MAIN_VERSION_ID,
    name: 'Main version',
    secret: false,
    seed: captureBoardSeed(session),
    audit: {
      createdAtIso: now,
      createdByUserId: actor?.userId ?? null,
      createdByName: actor?.name ?? null,
      lastEditedAtIso: now,
      lastEditedByUserId: actor?.userId ?? null,
      lastEditedByName: actor?.name ?? null,
    },
  }
  return {
    ...session,
    boardVersions: [main],
    selectedVersionId: main.id,
    exerciseRuns: session.exerciseRuns ?? [],
  }
}

export function cloneBoardVersion(
  session: ScenarioSession,
  sourceVersionId: string,
  name: string,
  actor?: { userId?: string | null; name?: string | null }
): ScenarioSession {
  const ensured = ensureBoardVersions(session, actor)
  const source = findBoardVersion(ensured.boardVersions, sourceVersionId)
  if (!source) return ensured
  const now = new Date().toISOString()
  const clone: BoardVersion = {
    id: newVersionId('version'),
    name: name.trim() || `${source.name} copy`,
    summary: source.summary,
    secret: false,
    dirty: false,
    seed: structuredClone(source.seed),
    audit: {
      createdAtIso: now,
      createdByUserId: actor?.userId ?? null,
      createdByName: actor?.name ?? null,
      clonedFromVersionId: source.id,
      clonedAtIso: now,
      lastEditedAtIso: now,
      lastEditedByUserId: actor?.userId ?? null,
      lastEditedByName: actor?.name ?? null,
    },
  }
  return {
    ...ensured,
    boardVersions: [...(ensured.boardVersions ?? []), clone],
    selectedVersionId: clone.id,
  }
}

/** Mark the selected version dirty after a real plan edit (objects / timeline / seed). */
export function markSelectedVersionDirty(session: ScenarioSession): ScenarioSession {
  const id = session.selectedVersionId
  if (!id) return session
  let changed = false
  const boardVersions = (session.boardVersions ?? []).map((v) => {
    if (v.id !== id || v.dirty !== false) return v
    changed = true
    return { ...v, dirty: true }
  })
  return changed ? { ...session, boardVersions } : session
}

/** True when the selected version seed already matches the working board (ignore audit). */
export function selectedVersionSeedMatches(session: ScenarioSession): boolean {
  const ver = selectedBoardVersion(session)
  if (!ver) return false
  return JSON.stringify(ver.seed) === JSON.stringify(captureBoardSeed(session))
}

/** Persist current working board into the selected version's seed (pencils-down / promote). */
export function updateSelectedVersionSeed(
  session: ScenarioSession,
  actor?: { userId?: string | null; name?: string | null }
): ScenarioSession {
  const ensured = ensureBoardVersions(session, actor)
  const id = ensured.selectedVersionId
  if (!id) return ensured
  const now = new Date().toISOString()
  const seed = captureBoardSeed(ensured)
  return {
    ...ensured,
    boardVersions: (ensured.boardVersions ?? []).map((v) =>
      v.id === id
        ? {
            ...v,
            dirty: true,
            seed,
            audit: {
              ...v.audit,
              lastEditedAtIso: now,
              lastEditedByUserId: actor?.userId ?? null,
              lastEditedByName: actor?.name ?? null,
            },
          }
        : v
    ),
  }
}

export function startExerciseRun(
  session: ScenarioSession,
  opts: {
    actor?: { userId?: string | null; name?: string | null }
    slug?: string | null
    actionsSeqStart?: number | null
    /** Continue from dirty working board without restoring seed. */
    continueDirty?: boolean
  } = {}
): { session: ScenarioSession; run: ExerciseRun } {
  const ensured = ensureBoardVersions(session, opts.actor)
  const versionId = ensured.selectedVersionId ?? MAIN_VERSION_ID
  const version = findBoardVersion(ensured.boardVersions, versionId)
  const runs = [...(ensured.exerciseRuns ?? [])]
  const number = runs.reduce((max, r) => Math.max(max, r.number), 0) + 1
  const now = new Date().toISOString()
  // Only one live take at a time — close any stuck in_progress so prior runs stay reviewable.
  for (let i = 0; i < runs.length; i++) {
    const prev = runs[i]!
    if (prev.status !== 'in_progress') continue
    runs[i] = {
      ...prev,
      status: 'abandoned',
      stoppedAtIso: now,
      stoppedByUserId: opts.actor?.userId ?? null,
      stoppedByName: opts.actor?.name ?? null,
    }
  }
  let board = ensured
  if (!opts.continueDirty && version) {
    board = applyBoardSeed(ensured, version.seed)
  }
  // Snapshot the board the run actually starts from (post seed apply or continue-dirty).
  const runId = newRunId()
  const runBase: ExerciseRun = {
    id: runId,
    number,
    slug: opts.slug?.trim() || null,
    versionId,
    status: 'in_progress',
    startedAtIso: now,
    startedByUserId: opts.actor?.userId ?? null,
    startedByName: opts.actor?.name ?? null,
    actionsSeqStart: opts.actionsSeqStart ?? null,
    seed: captureBoardSeed(board),
  }
  const run: ExerciseRun = {
    ...runBase,
    comments: buildChatForNewRun(board, runBase, runs),
  }
  const next: ScenarioSession = {
    ...board,
    exerciseRuns: [...runs, run],
    exerciseTakeNumber: number,
    exerciseTakeStopped: false,
  }
  return { session: next, run }
}

export function stopExerciseRun(
  session: ScenarioSession,
  opts: {
    actor?: { userId?: string | null; name?: string | null }
    actionsSeqEnd?: number | null
    /** Story offset at stop / story-end — tape review blackout boundary. */
    stoppedStoryElapsedMs?: number | null
    abandoned?: boolean
    /**
     * Default false — leave the board as the finished run so the room can Watch.
     * Seed restore happens when starting the next run / switching story / making a copy.
     */
    resetToSeed?: boolean
  } = {}
): ScenarioSession {
  const ensured = ensureBoardVersions(session, opts.actor)
  const runs = [...(ensured.exerciseRuns ?? [])]
  const idx = [...runs].reverse().findIndex((r) => r.status === 'in_progress')
  const realIdx = idx < 0 ? -1 : runs.length - 1 - idx
  const now = new Date().toISOString()
  const stoppedMs =
    opts.stoppedStoryElapsedMs != null && Number.isFinite(opts.stoppedStoryElapsedMs)
      ? Math.max(0, Math.floor(opts.stoppedStoryElapsedMs))
      : null
  if (realIdx >= 0) {
    // Close every in-progress row (stuck priors stay Instant Replay targets).
    for (let i = 0; i < runs.length; i++) {
      const prev = runs[i]!
      if (prev.status !== 'in_progress') continue
      const isLatest = i === realIdx
      runs[i] = {
        ...prev,
        status: isLatest
          ? opts.abandoned
            ? 'abandoned'
            : 'completed'
          : 'abandoned',
        stoppedAtIso: now,
        stoppedByUserId: opts.actor?.userId ?? null,
        stoppedByName: opts.actor?.name ?? null,
        actionsSeqEnd: isLatest ? (opts.actionsSeqEnd ?? null) : prev.actionsSeqEnd ?? null,
        stoppedStoryElapsedMs: isLatest
          ? (stoppedMs ?? prev.stoppedStoryElapsedMs ?? null)
          : prev.stoppedStoryElapsedMs ?? null,
      }
    }
  } else if ((ensured.exerciseTakeNumber ?? 0) > 0) {
    // Heal: take phase advanced (Start/Resume) but exerciseRuns never landed
    // (failed persist / ApplyBoardSeed race). Without this, Stop leaves Reviewing
    // with no run and the strip falls back to Previewing.
    const versionId = ensured.selectedVersionId ?? MAIN_VERSION_ID
    const number = Math.max(1, ensured.exerciseTakeNumber ?? 1)
    const already =
      runs.find((r) => r.number === number && r.versionId === versionId) ?? null
    if (!already) {
      runs.push({
        id: newRunId(),
        number,
        slug: null,
        versionId,
        status: opts.abandoned ? 'abandoned' : 'completed',
        startedAtIso: now,
        startedByUserId: opts.actor?.userId ?? null,
        startedByName: opts.actor?.name ?? null,
        stoppedAtIso: now,
        stoppedByUserId: opts.actor?.userId ?? null,
        stoppedByName: opts.actor?.name ?? null,
        actionsSeqStart: null,
        actionsSeqEnd: opts.actionsSeqEnd ?? null,
        stoppedStoryElapsedMs: stoppedMs,
      })
    }
  }
  let next: ScenarioSession = {
    ...ensured,
    exerciseRuns: runs,
    exerciseTakeStopped: true,
    exerciseTakeNumber: Math.max(
      1,
      ensured.exerciseTakeNumber ?? runs[runs.length - 1]?.number ?? 1
    ),
  }
  if (opts.resetToSeed === true) {
    const version = selectedBoardVersion(next)
    if (version) next = applyBoardSeed(next, version.seed)
  }
  return next
}

export function setRunSlug(
  session: ScenarioSession,
  runId: string,
  slug: string | null
): ScenarioSession {
  return {
    ...session,
    exerciseRuns: (session.exerciseRuns ?? []).map((r) =>
      r.id === runId ? { ...r, slug: slug?.trim() || null } : r
    ),
  }
}

export function activeExerciseRun(session: ScenarioSession): ExerciseRun | null {
  const live = (session.exerciseRuns ?? []).filter((r) => r.status === 'in_progress')
  if (!live.length) return null
  const take =
    typeof session.exerciseTakeNumber === 'number' && session.exerciseTakeNumber > 0
      ? Math.floor(session.exerciseTakeNumber)
      : 0
  // Prefer the hub take — oldest stuck in_progress must not win over Run N.
  if (take > 0) {
    const match = live.find((r) => r.number === take)
    if (match) return match
  }
  return live.reduce((a, b) => (a.number >= b.number ? a : b))
}

/**
 * Peer Board `startRun` may omit bulky per-run seeds so the SignalR payload stays small.
 * Keep local seed / comments for known run ids when the wire row leaves them out.
 */
export function mergeExerciseRunsFromPeer(
  local: ExerciseRun[] | undefined,
  wire: ExerciseRun[]
): ExerciseRun[] {
  const prevById = new Map((local ?? []).map((r) => [r.id, r]))
  return wire.map((r) => {
    const prev = prevById.get(r.id)
    if (!prev) return r
    return {
      ...r,
      seed: r.seed ?? prev.seed,
      comments:
        r.comments && r.comments.length > 0 ? r.comments : prev.comments,
      // Lean startRun / partial Fields may omit the tape stop stamp — keep local.
      stoppedStoryElapsedMs:
        r.stoppedStoryElapsedMs != null && Number.isFinite(r.stoppedStoryElapsedMs)
          ? r.stoppedStoryElapsedMs
          : prev.stoppedStoryElapsedMs,
    }
  })
}

/**
 * When hub take# advances (or a take is stopped) but a peer missed `startRun`,
 * close local in_progress rows that no longer match so strip/chips cannot stick on Run 1.
 */
export function healRunsForHubTake(
  session: ScenarioSession,
  takeNumber: number,
  stopped: boolean
): ScenarioSession {
  const take = Math.max(0, Math.floor(takeNumber))
  if (take <= 0) return session
  const runs = [...(session.exerciseRuns ?? [])]
  let changed = false
  const now = new Date().toISOString()
  for (let i = 0; i < runs.length; i++) {
    const r = runs[i]!
    if (r.status !== 'in_progress') continue
    if (!stopped && r.number === take) continue
    runs[i] = {
      ...r,
      status: 'abandoned',
      stoppedAtIso: r.stoppedAtIso ?? now,
    }
    changed = true
  }
  if (!changed) return session
  return { ...session, exerciseRuns: runs }
}

export function latestExerciseRun(session: ScenarioSession): ExerciseRun | null {
  const runs = session.exerciseRuns ?? []
  if (!runs.length) return null
  return runs.reduce((a, b) => (a.number >= b.number ? a : b))
}

/** Latest run on a version (by session-global `number`). Defaults to selectedVersionId. */
export function latestExerciseRunForVersion(
  session: ScenarioSession,
  versionId?: string | null,
): ExerciseRun | null {
  const vid = (versionId ?? session.selectedVersionId ?? '').trim()
  const runs = (session.exerciseRuns ?? []).filter((r) =>
    vid ? r.versionId === vid : true,
  )
  if (!runs.length) return null
  return runs.reduce((a, b) => (a.number >= b.number ? a : b))
}

/**
 * 1-based ordinal of `run` among runs on its version (sorted by global `number`).
 * Strip / toast / chips must use this — not session-global `run.number` or take#.
 */
export function versionLocalRunNumber(
  session: ScenarioSession,
  run: Pick<ExerciseRun, 'number' | 'versionId'> | null | undefined,
): number {
  if (!run) return 0
  const vid = (run.versionId || session.selectedVersionId || '').trim()
  const runs = (session.exerciseRuns ?? [])
    .filter((r) => (vid ? r.versionId === vid : true))
    .slice()
    .sort((a, b) => a.number - b.number)
  const n = runs.reduce((acc, r) => (r.number <= run.number ? acc + 1 : acc), 0)
  return n || 1
}

export function formatRunLabel(run: ExerciseRun | null | undefined): string {
  if (!run) return 'No runs yet'
  const slug = run.slug?.trim()
  return slug ? `Run ${run.number} · ${slug}` : `Run ${run.number}`
}

/**
 * Shift every story-relative time by deltaMs (timeline.at, object bounds, pose keyframes, camera beats).
 * Prefer {@link shiftSeedFromMs} when only a late beat should move (keeps early map presence).
 */
export function shiftSeedByMs(seed: BoardVersionSeed, deltaMs: number): BoardVersionSeed {
  return shiftSeedFromMs(seed, Number.NEGATIVE_INFINITY, deltaMs)
}

/**
 * Shift story times at or after `fromMs` (Date.parse epoch) by deltaMs.
 * Instants before the gate stay put so departure / early climb stay visible at story start.
 */
export function shiftSeedFromMs(
  seed: BoardVersionSeed,
  fromMs: number,
  deltaMs: number,
): BoardVersionSeed {
  if (!deltaMs) return structuredClone(seed)
  const shiftIf = (iso: string | undefined): string | undefined => {
    if (!iso) return iso
    const t = Date.parse(iso)
    if (!Number.isFinite(t) || t < fromMs) return iso
    return new Date(t + deltaMs).toISOString()
  }
  return {
    ...structuredClone(seed),
    objects: (seed.objects ?? []).map((o) => ({
      ...o,
      startsAt: shiftIf(o.startsAt) ?? o.startsAt,
      endsAt: shiftIf(o.endsAt) ?? o.endsAt,
      keyframes: o.keyframes?.map((k) => ({ ...k, at: shiftIf(k.at) ?? k.at })),
    })),
    timeline: (seed.timeline ?? []).map((ev) => ({
      ...ev,
      at: shiftIf(ev.at) ?? ev.at,
    })),
    cameraBeats: seed.cameraBeats?.map((b) => ({
      ...b,
      at: shiftIf(b.at) ?? b.at,
    })),
  }
}

/** Legacy timeline beat id — stripped on heal; variation blurb lives on BoardVersion.summary. */
export const SULLY_PLUS_30_MARKER_EVENT_ID = 'ev-sully-plus-30-marker'

export const BIRD_STRIKE_LATER_VERSION_ID = 'version-bird-strike-later'

export const MAIN_VERSION_DISPLAY_NAME = 'Main version (historical)'
export const SULLY_VERSION_DISPLAY_NAME = 'Sully delays ditch call by 30 secs'
export const BIRD_LATER_DISPLAY_NAME = 'Bird strike occurs 45 secs later → NJ'

/** Canonical Main / session overview (no em dashes). */
export const HUDSON_MAIN_SUMMARY =
  'Bird strike after LaGuardia takeoff, ditch in the Hudson, ferry and Coast Guard rescue. All 155 survive.'

export const SULLY_VERSION_SUMMARY =
  'What-if: ditch call about 30 seconds later than the historical Hudson landing.'

export const BIRD_LATER_SUMMARY =
  'What-if: bird strike about 45 seconds later leaves no clean river option; aircraft comes down in New Jersey.'

/**
 * Wreck / hot-zone center (teaching pin). Keep distinct from the engine stop so
 * the apparatus does not drive into the keep-out center.
 */
const NJ_CRASH = { lng: -74.0285, lat: 40.7678 }
/** Engine staging + arrive (author pins — exact; linear path, no inventing waypoints). */
const NJ_ENGINE_START = { lng: -74.039629, lat: 40.751318 }
const NJ_ENGINE_ARRIVE = { lng: -74.030244, lat: 40.766005 }
/** Match Main post-bird average ground speed (~150 km/h) when spacing divert pins. */
const BIRD_DIVERT_TARGET_KMH = 150
const SHIFT_BIRD_LATER_MS = 45_000
const SHIFT_SULLY_MS = 30_000
const ENGINE_ARRIVE_ID = 'ev-engine-arrive'
const SULLY_CUE_LABEL_ID = 'sully-delay-cue'
const BIRD_CUE_LABEL_ID = 'bird-later-cue'
const ENGINE_OBJECT_ID = 'engine-nj-1'
/** Ghost of the historical Hudson track under the Bird-strike divert. */
const INTENDED_FLIGHT_PATH_ID = 'flight-path-intended'
function haversineKm(
  a: { lng: number; lat: number },
  b: { lng: number; lat: number }
): number {
  const R = 6371
  const toR = (x: number) => (x * Math.PI) / 180
  const dLat = toR(b.lat - a.lat)
  const dLng = toR(b.lng - a.lng)
  const la1 = toR(a.lat)
  const la2 = toR(b.lat)
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(h))
}

/** Equal-distance samples along a→b (inclusive). */
function sampleLineEqual(
  a: { lng: number; lat: number },
  b: { lng: number; lat: number },
  segments: number
): { lng: number; lat: number }[] {
  const n = Math.max(1, segments)
  const out: { lng: number; lat: number }[] = []
  for (let i = 0; i <= n; i++) {
    const t = i / n
    out.push({
      lng: a.lng + (b.lng - a.lng) * t,
      lat: a.lat + (b.lat - a.lat) * t,
    })
  }
  return out
}

/** Evenly spaced pins along a polyline; times from distance at targetKmH. */
function pacePathKeyframes(
  points: { lng: number; lat: number }[],
  startAtIso: string,
  targetKmH: number
): { at: string; lng: number; lat: number }[] {
  if (points.length === 0) return []
  const out: { at: string; lng: number; lat: number }[] = [
    { at: startAtIso, ...points[0]! },
  ]
  let elapsedMs = 0
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1]!
    const cur = points[i]!
    const km = haversineKm(prev, cur)
    const ms = Math.max(1_000, Math.round((km / targetKmH) * 3_600_000))
    elapsedMs += ms
    const at = shiftIso(startAtIso, elapsedMs)
    if (!at) break
    out.push({ at, ...cur })
  }
  return out
}

function stripEmDashes(text: string): string {
  return text.replace(/\u2014/g, ',').replace(/\s+,/g, ',').replace(/,\s*,/g, ',')
}

function stripPlusOffsetSuffix(title: string): string {
  return title.replace(/\s*\(\+\d+s\)\s*$/i, '').trim()
}

/**
 * Build the Sully variation seed from Main.
 * Delay only the ditch call (mayday) and everything at/after it by +30s — do NOT
 * shift departure / climb / bird-strike. A whole-seed shift left plane/path
 * startsAt 30s past storyStartTime, so version switch (elapsed→0) blanked the map
 * while Timeline cards still listed.
 */
export function buildSullyPlus30Seed(mainSeed: BoardVersionSeed): BoardVersionSeed {
  const maydayGate = (mainSeed.timeline ?? []).find((ev) => ev.id === 'ev-mayday')
  const gateMs = maydayGate?.at ? Date.parse(maydayGate.at) : NaN
  const plus30 = Number.isFinite(gateMs)
    ? shiftSeedFromMs(mainSeed, gateMs, SHIFT_SULLY_MS)
    : structuredClone(mainSeed)
  plus30.objects = (plus30.objects ?? [])
    .filter((o) => o.id !== SULLY_CUE_LABEL_ID && o.id !== BIRD_CUE_LABEL_ID)
    .map((o) => {
      let next = { ...o }
      // Strip legacy (+30s) / NJ tags so titles stay constant across versions.
      if (o.id === 'airplane-1' || o.id === 'ditch-label') {
        const baseLabel = (o.label ?? 'US Airways 1549')
          .replace(/\s*\([^)]*\)\s*$/i, '')
          .replace(/\s*\+?\d+s\s*$/i, '')
          .trim()
        const baseMap = (o.mapLabel ?? '1549')
          .replace(/\s*\+?\d+s\s*$/i, '')
          .replace(/\s*NJ\s*$/i, '')
          .trim()
        const params = o.params ? { ...o.params } : undefined
        if (params && typeof params.text === 'string') {
          params.text = String(params.text)
            .replace(/\s*\([^)]*\)\s*$/i, '')
            .replace(/\s*\+?\d+s\s*$/i, '')
            .trim()
        }
        next = {
          ...next,
          label: baseLabel || 'US Airways 1549',
          mapLabel: baseMap || '1549',
          ...(params ? { params } : {}),
        }
      }
      if (o.id === 'flight-path-1') {
        next = { ...next, params: { ...(next.params ?? {}), keepOpacity: true } }
      }
      return next
    })
  const mayday = (plus30.timeline ?? []).find((ev) => ev.id === 'ev-mayday')
  const ditch = (plus30.timeline ?? []).find((ev) => ev.id === 'ev-ditch')
  const plane = (plus30.objects ?? []).find((o) => o.id === 'airplane-1')
  const cueAt = mayday?.at ?? ditch?.at
  const cueMs = cueAt ? Date.parse(cueAt) : NaN
  const maydayPin =
    Number.isFinite(cueMs) && plane?.keyframes?.length
      ? [...plane.keyframes]
          .filter((k) => Number.isFinite(Date.parse(k.at)) && Date.parse(k.at) <= cueMs)
          .sort((a, b) => Date.parse(a.at) - Date.parse(b.at))
          .at(-1)
      : undefined
  const cuePose = maydayPin ?? plane?.pose ?? { lng: -74.0, lat: 40.77 }
  if (cueAt) {
    plus30.objects.push({
      id: SULLY_CUE_LABEL_ID,
      type: 'label',
      label: 'Delayed ditch call',
      scope: 'local',
      pose: {
        lng: cuePose.lng,
        lat: cuePose.lat + 0.0045,
      },
      size: 'md',
      description: 'What-if cue: ditch decision about 30 seconds later than historical.',
      startsAt: cueAt,
      endsAt: ditch?.at,
      showOnTimeline: false,
      params: { text: 'Delayed ditch call (+30s)', color: '#ffffff' },
      comments: [],
    })
  }
  plus30.timeline = (plus30.timeline ?? [])
    .filter((ev) => ev.id !== SULLY_PLUS_30_MARKER_EVENT_ID)
    .map((ev) => ({ ...ev, title: stripPlusOffsetSuffix(ev.title) }))
  return plus30
}

function shiftIso(iso: string | undefined, deltaMs: number): string | undefined {
  if (!iso) return iso
  const t = Date.parse(iso)
  if (!Number.isFinite(t)) return iso
  return new Date(t + deltaMs).toISOString()
}

/**
 * What-if: bird strike ~45s later — past a clean Hudson ditch energy window.
 * Teaching sketch: path turns into New Jersey; Engine rolls from Palisade & 5th.
 * Ferries / marine stay at their original Hudson water pins — never on land.
 */
export function buildBirdStrikeLaterSeed(mainSeed: BoardVersionSeed): BoardVersionSeed {
  const seed = structuredClone(mainSeed)
  const birdEv = (seed.timeline ?? []).find((ev) => ev.id === 'ev-birds')
  const birdAt = birdEv?.at
  const birdMs = birdAt ? Date.parse(birdAt) : NaN
  const birdLaterAt = shiftIso(birdAt, SHIFT_BIRD_LATER_MS)
  const maydayAt = shiftIso(birdAt, 60_000)

  const pathObj = (seed.objects ?? []).find((o) => o.id === 'flight-path-1')
  const historicalPath = (pathObj?.path ?? []) as [number, number][]
  const climbPath = historicalPath.slice(0, 8)
  const climbEnd = {
    lng: climbPath[climbPath.length - 1]?.[0] ?? -73.905,
    lat: climbPath[climbPath.length - 1]?.[1] ?? 40.812,
  }
  // Equal-distance samples mayday→wreck so every hop (incl. the last) matches Main glide speed.
  const maydayPos = { lng: climbEnd.lng - 0.006, lat: climbEnd.lat - 0.0015 }
  const divertSpine = sampleLineEqual(maydayPos, NJ_CRASH, 8)
  const pacedFromMayday = maydayAt
    ? pacePathKeyframes(divertSpine, maydayAt, BIRD_DIVERT_TARGET_KMH)
    : []
  const impactAt = pacedFromMayday[pacedFromMayday.length - 1]?.at ?? shiftIso(birdAt, 6 * 60_000)
  const engineAppearAt = shiftIso(impactAt, 90_000) // stage ~1.5 min after impact
  const engineAt = shiftIso(impactAt, 3 * 60_000 + 30_000) // arrive a few min after impact
  const planeHoldAt = engineAt ?? impactAt

  // Rebuild timeline: drop shore / ferry / evacuate; end at Engine.
  const keepIds = new Set(['ev-depart', 'ev-birds', 'ev-mayday', 'ev-ditch'])
  const rebuilt = (seed.timeline ?? [])
    .filter((ev) => keepIds.has(ev.id) && ev.id !== SULLY_PLUS_30_MARKER_EVENT_ID)
    .map((ev) => {
      if (ev.id === 'ev-depart') {
        return {
          ...ev,
          title: stripPlusOffsetSuffix(ev.title),
          kind: 'narration' as const,
          actions: (ev.actions ?? []).filter((a) => a.kind === 'setParams'),
        }
      }
      if (ev.id === 'ev-birds') {
        return {
          ...ev,
          at: birdLaterAt ?? ev.at,
          title: 'Bird strike',
          summary: 'Geese hit later in the climb. Less altitude and energy than the historical case.',
          kind: 'narration' as const,
          actions: (ev.actions ?? []).filter((a) => a.kind === 'setParams'),
        }
      }
      if (ev.id === 'ev-mayday') {
        return {
          ...ev,
          at: maydayAt ?? ev.at,
          title: 'Mayday - no clean river',
          summary:
            'Crew looks for a runway. Position and energy no longer favor a controlled Hudson ditch.',
          kind: 'narration' as const,
          // Keep ditch-call color (and any other beat setParams) from Main.
          actions: (ev.actions ?? []).filter((a) => a.kind === 'setParams'),
        }
      }
      if (ev.id === 'ev-ditch') {
        return {
          ...ev,
          at: impactAt ?? ev.at,
          title: 'Impact in New Jersey',
          summary: 'Aircraft comes down on the New Jersey side. Hot zone opens around the wreckage.',
          kind: 'narration' as const,
          actions: [],
        }
      }
      return {
        ...ev,
        kind: 'narration' as const,
        actions: (ev.actions ?? []).filter((a) => a.kind === 'setParams'),
      }
    })
  rebuilt.push({
    id: ENGINE_ARRIVE_ID,
    at: engineAt ?? impactAt ?? birdLaterAt ?? new Date().toISOString(),
    title: 'Engine arrives on scene',
    summary: 'First engine company reaches the New Jersey impact site.',
    iconType: 'engine',
    hiddenFromParticipants: false,
    kind: 'narration',
    actions: [],
  })
  seed.timeline = rebuilt

  const njPath: [number, number][] = [
    ...climbPath,
    ...divertSpine.map((p) => [p.lng, p.lat] as [number, number]),
  ]

  seed.objects = (seed.objects ?? [])
    .filter((o) => o.id !== ENGINE_OBJECT_ID && o.id !== INTENDED_FLIGHT_PATH_ID)
    .map((o) => {
      let next = { ...o }
      if (o.id === 'flight-path-1') {
        next = {
          ...next,
          label: 'What-if path (NJ)',
          mapLabel: 'Divert',
          path: njPath,
          description:
            'What-if track: later bird strike, turn toward New Jersey, ground impact (teaching sketch).',
          params: {
            ...(o.params ?? {}),
            color: '#5B8DEF',
            width: 3,
            opacity: 0.85,
            keepOpacity: true,
          },
        }
      }
      if (o.id === 'airplane-1' && o.keyframes?.length && Number.isFinite(birdMs)) {
        const pre = o.keyframes.filter((k) => Date.parse(k.at) < birdMs - 1)
        const birdPin = {
          at: birdLaterAt ?? o.keyframes[pre.length]?.at ?? o.keyframes[0]!.at,
          lng: climbEnd.lng,
          lat: climbEnd.lat,
        }
        // Hold on the wreck after impact so the engine beat has a parked plane.
        const hold =
          impactAt && planeHoldAt
            ? [{ at: planeHoldAt, lng: NJ_CRASH.lng, lat: NJ_CRASH.lat }]
            : []
        next = {
          ...next,
          label: 'US Airways 1549',
          mapLabel: '1549',
          keyframes: [...pre, birdPin, ...pacedFromMayday, ...hold],
          description:
            'What-if tracker: bird strike later in climb, then New Jersey impact instead of Hudson ditch.',
        }
      }
      if (o.id === 'ditch-label' || o.id === 'hot-1') {
        next = {
          ...next,
          pose: {
            lng: NJ_CRASH.lng + (o.id === 'ditch-label' ? 0.0012 : 0),
            lat: NJ_CRASH.lat + (o.id === 'ditch-label' ? 0.0008 : 0),
          },
          startsAt: impactAt ?? o.startsAt,
          // Keep Main-style titles; NJ is signaled by the cue label, not object suffixes.
          label: o.id === 'ditch-label' ? 'US Airways 1549' : 'Crash keep-out',
          mapLabel: o.id === 'ditch-label' ? '1549' : 'Hot zone',
          description:
            o.id === 'ditch-label'
              ? 'What-if impact site - New Jersey shore (approximate teaching pin).'
              : 'Keep-out around the New Jersey impact site.',
          params:
            o.id === 'ditch-label'
              ? { ...(o.params ?? {}), text: 'US Airways 1549', color: '#ffffff' }
              : o.params,
        }
      }
      // Ferries / marine belong to the Hudson ditch story — hide entirely (were appearing on land).
      if (o.id === 'ferry-w' || o.id === 'ferry-e' || o.id === 'cg-boat') {
        next = {
          ...next,
          keyframes: [],
          startsAt: undefined,
          endsAt: undefined,
          showOnTimeline: false,
          ignored: true,
        }
      }
      // Shore stack from the ditch story — hide from this variation (Engine is the response beat).
      if (
        o.id === 'icp-1' ||
        o.id === 'staging-1' ||
        o.id === 'muster-1' ||
        o.id === 'ems-1' ||
        o.id === 'le-1'
      ) {
        next = {
          ...next,
          startsAt: undefined,
          endsAt: undefined,
          showOnTimeline: false,
          ignored: true,
        }
      }
      return next
    })

  // Historical Hudson track as a dashed ghost — surprise when the plane leaves it.
  if (historicalPath.length >= 2) {
    seed.objects.push({
      id: INTENDED_FLIGHT_PATH_ID,
      type: 'sketch',
      label: 'Intended path (Hudson)',
      mapLabel: 'Intended',
      scope: 'local',
      pose: {
        lng: historicalPath[0]?.[0] ?? -73.874,
        lat: historicalPath[0]?.[1] ?? 40.7769,
      },
      size: 'md',
      path: historicalPath,
      description:
        'Original intended track toward a Hudson ditch — shown dashed under the what-if divert.',
      startsAt: pathObj?.startsAt,
      showOnTimeline: false,
      params: {
        color: '#8a9bb0',
        width: 2.5,
        opacity: 0.55,
        dashed: true,
        keepOpacity: true,
      },
      comments: [],
    })
  }

  seed.objects.push({
    id: ENGINE_OBJECT_ID,
    type: 'engine',
    label: 'Engine 1',
    mapLabel: 'Engine 1',
    scope: 'local',
    pose: NJ_ENGINE_START,
    size: 'lg',
    description: 'Engine company staging south of the impact, rolling toward the wreckage.',
    startsAt: engineAppearAt ?? engineAt,
    showOnTimeline: false,
    params: {},
    // Author marks only — linear start→arrive, not into the hot-zone center.
    keyframes: [
      { at: engineAppearAt ?? engineAt ?? new Date().toISOString(), ...NJ_ENGINE_START },
      { at: engineAt ?? new Date().toISOString(), ...NJ_ENGINE_ARRIVE },
    ],
    comments: [],
  })

  seed.objects = (seed.objects ?? []).filter(
    (o) => o.id !== BIRD_CUE_LABEL_ID && o.id !== SULLY_CUE_LABEL_ID
  )
  if (birdLaterAt) {
    seed.objects.push({
      id: BIRD_CUE_LABEL_ID,
      type: 'label',
      label: 'Later bird strike',
      scope: 'local',
      pose: { lng: climbEnd.lng, lat: climbEnd.lat + 0.004 },
      size: 'md',
      description: 'What-if cue: bird strike about 45 seconds later than historical.',
      startsAt: birdLaterAt,
      // Stay visible once shown — do not clear mid-timeline.
      endsAt: undefined,
      showOnTimeline: false,
      params: {
        text: 'Later bird strike (+45s)',
        color: '#ffffff',
      },
      comments: [],
    })
  }
  // Keep the wide overview through Mayday so the bird cue stays readable; only impact re-frames.
  seed.cameraBeats = seed.cameraBeats
    ?.filter(
      (b) =>
        b.eventId !== 'ev-shore' &&
        b.eventId !== 'ev-close' &&
        b.eventId !== 'ev-ferries' &&
        b.eventId !== 'ev-mayday'
    )
    .map((b) => {
      const at =
        b.eventId === 'ev-birds'
          ? birdLaterAt ?? b.at
          : b.eventId === 'ev-ditch'
            ? impactAt ?? b.at
            : b.at
      if (b.eventId === 'ev-ditch') {
        return { ...b, at, lng: NJ_CRASH.lng, lat: NJ_CRASH.lat, zoom: b.zoom ?? 14.2 }
      }
      return { ...b, at }
    })
  return seed
}

/** Build Main + Sully + bird-strike-later variations from a historical Hudson session. */
export function withHudsonDualVersions(session: ScenarioSession): ScenarioSession {
  return withHudsonBoardVersions(session)
}

export function withHudsonBoardVersions(session: ScenarioSession): ScenarioSession {
  const now = new Date().toISOString()
  const mainSeed = captureBoardSeed(session)
  // Keep route sketches readable while other events are timeline-lit.
  mainSeed.objects = (mainSeed.objects ?? []).map((o) =>
    o.id === 'flight-path-1'
      ? { ...o, params: { ...(o.params ?? {}), keepOpacity: true } }
      : o
  )
  const plus30 = buildSullyPlus30Seed(mainSeed)
  const birdLater = buildBirdStrikeLaterSeed(mainSeed)
  const mainSummary = stripEmDashes(session.summary?.trim() || HUDSON_MAIN_SUMMARY)
  const main: BoardVersion = {
    id: MAIN_VERSION_ID,
    name: MAIN_VERSION_DISPLAY_NAME,
    summary: mainSummary,
    secret: false,
    seed: mainSeed,
    audit: { createdAtIso: now, createdByName: 'Template' },
  }
  const sully: BoardVersion = {
    id: SULLY_PLUS_30_VERSION_ID,
    name: SULLY_VERSION_DISPLAY_NAME,
    summary: SULLY_VERSION_SUMMARY,
    secret: false,
    seed: plus30,
    audit: {
      createdAtIso: now,
      createdByName: 'Template',
      clonedFromVersionId: MAIN_VERSION_ID,
      clonedAtIso: now,
    },
  }
  const bird: BoardVersion = {
    id: BIRD_STRIKE_LATER_VERSION_ID,
    name: BIRD_LATER_DISPLAY_NAME,
    summary: BIRD_LATER_SUMMARY,
    secret: false,
    seed: birdLater,
    audit: {
      createdAtIso: now,
      createdByName: 'Template',
      clonedFromVersionId: MAIN_VERSION_ID,
      clonedAtIso: now,
    },
  }
  return {
    ...session,
    summary: mainSummary,
    boardVersions: [main, sully, bird],
    selectedVersionId: MAIN_VERSION_ID,
    exerciseRuns: [],
  }
}
